import {AxiosRequestConfig} from 'axios';
import {OrganizationService} from './organization.service';
import Logger from '../../logger';
import {Organization} from '../../models';
import {getAuthToken} from '../../utils';
import {APIServiceImpl} from '../api';
import {OrganizationCreateParams, OrganizationSearchParams} from './organization-params';

export class OrganizationServiceImpl extends APIServiceImpl implements OrganizationService {
  static readonly CREATE_ORGANIZATION = '/accounts/:accountId/organizations';
  static readonly SEARCH_ORGANIZATIONS = '/organizations';
  static readonly UPDATE_ORGANIZATION = '/accounts/:accountId/organizations/:organizationId';
  static readonly FIND_BY_SUBDOMAIN = '/organizations/subdomain/:subdomain';
  static readonly GET_ORGANIZATION_BY_ID = '/organizations/:organizationId';

  constructor() {
    super();
    this.service.interceptors.request.use((request): AxiosRequestConfig => {
      const token = getAuthToken();
      token && (request.headers.Authorization = `Bearer ${token}`);
      return request;
    });
  }

  async createOrganization(params: OrganizationCreateParams): Promise<Organization> {
    const url = OrganizationServiceImpl.CREATE_ORGANIZATION.replace(':accountId', params.accountId);
    try {
      const {data: res} = await this.post(url, {
        name: params.name,
        logoImg: params.logoImg,
        portalUrl: params.portalUrl,
      });
      return new Organization(res);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`POST ${url} ${JSON.stringify(params)}: ${e.toString()}: ${error}`);
      throw new Error(error);
    }
  }

  async searchOrganizations(params: OrganizationSearchParams): Promise<Organization[]> {
    const url = OrganizationServiceImpl.SEARCH_ORGANIZATIONS;
    const queryParams = {
      q: params.query,
      limit: params.limit,
      skip: params.skip,
      status: params.status,
      subdomain: params.subdomain,
    };

    // Remove undefined values
    const filteredParams = Object.fromEntries(Object.entries(queryParams).filter(([_, value]) => value !== undefined));

    try {
      const {data: res} = await this.get(url, {params: filteredParams});
      return res.map((org: any) => new Organization(org));
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`GET ${url} with ${JSON.stringify(filteredParams)}: ${e.toString()}: ${error}`);
      throw new Error(error);
    }
  }

  async updateOrganization(organizationId: string, accountId: string, updates: Partial<Organization>): Promise<Organization> {
    const url = OrganizationServiceImpl.UPDATE_ORGANIZATION.replace(':accountId', accountId).replace(':organizationId', organizationId);

    try {
      const {data: res} = await this.patch(url, updates);
      return new Organization(res);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`PATCH ${url} ${JSON.stringify(updates)}: ${e.toString()}: ${error}`);
      throw new Error(error);
    }
  }

  async getOrganizationById(organizationId: string): Promise<Organization> {
    const url = OrganizationServiceImpl.GET_ORGANIZATION_BY_ID.replace(':organizationId', organizationId);

    try {
      const {data: res} = await this.get(url);
      return new Organization(res);
    } catch (e) {
      const error = e.response?.data?.message || e.message || e.response?.status;
      Logger.error(`GET ${url}: ${e.toString()}: ${error}`);
      throw new Error(error);
    }
  }
}
